/**
 * La compra en una sola pantalla: del remito leído a «Cargar».
 *
 * Reemplaza los tres pasos (¿es esto? → los productos → revisar). Salió del
 * prototipo que probaron usuarios simulados el 29/10/2026 (la variante D):
 *
 *   · Arriba, quién y cuánto, y «¿La pagaste?» con cuatro botones sin ninguno
 *     marcado. Con «cuenta corriente» elegida de entrada, el que no miraba
 *     dejaba una deuda que ya había pagado.
 *   · Lo que se parece a un producto se pregunta de a una («¿Es tu X?»).
 *   · Lo que no se parece a nada se propone como nuevo, con el nombre del
 *     catálogo, el rubro de las otras líneas y su margen habitual.
 *   · Un renglón por producto con una sola marca que habla del precio: sube,
 *     no baja, sin cambios, nuevo, vuelve a la venta.
 *   · Con una factura larga solo queda a la vista lo que cambia; lo que repone
 *     igual se pliega en una línea.
 *   · Un botón que dice adónde va la plata. No hay «¿Seguro?»: hay Deshacer.
 */
import { useMemo, useState } from "react";
import { formatearNumero, parsearImporte, type Moneda } from "@tora/shared";
import { Check, CheckCheck, ChevronRight, PlusCircle, Search, TriangleAlert } from "lucide-react";

import { mostrarCantidad } from "@/components/catalogo/Campos";
import type { CuentaFinanciera } from "@/components/cobro/logica";
import { ChipMoneda, Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero, type Proveedor, type Rubro } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import { AltaProducto } from "./AltaProducto";
import type { CambiosFila, NuevaFila } from "./consultas";
import { LineaManual } from "./LineaManual";
import {
  LINEAS_SIN_PLEGAR,
  TEXTO_MARCA,
  altaPropuesta,
  cambiosDeAlta,
  cantidadEfectiva,
  costoDelProducto,
  cuentaParaPagar,
  esManual,
  esParaMirar,
  esPregunta,
  esPropuestaNueva,
  cambiosDePrecio,
  marcaConPrecio,
  marcaDeLinea,
  margenDelPrecio,
  precioDelRenglon,
  precioEnElCampo,
  precioRevisado,
  referenciaDe,
  textoDeLaForma,
  type PrecioDelRenglon,
  type Compra,
  type DatosAlta,
  type FilaCompra,
  type FormaDePago,
  type MarcaLinea,
  type ResumenAplicar,
  type VerificacionTotal,
} from "./logica";
import type { ResumenDeCarga } from "./ResumenDeCarga";
import { CantidadRecibida, OtroPrecio } from "./TarjetaLinea";

/** «OTRA» es el bloque de deuda completo: una parte, varias cajas, otro importe. */
export type FormaElegida = FormaDePago | "OTRA";

export interface PropuestaParaGuardar {
  filaId: number;
  cambios: CambiosFila;
}

export interface CompraEnUnaPantallaProps {
  compra: Compra;
  filas: ReadonlyArray<FilaCompra>;
  proveedor: Proveedor | null;
  rubros: ReadonlyArray<Rubro>;
  rubroSugeridoId: number | null;
  margenes: ReadonlyMap<number, number>;
  cotizacion: number;
  monedaProveedor: Moneda | null;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  resumen: ResumenAplicar;
  verificacion: VerificacionTotal;
  /** A quién le falta el dólar de la factura, o null si no hace falta. */
  sinCotizacionDe: string | null;
  encargosSinLeer: boolean;
  /** El importe sospechoso de la deuda todavía no se miró (BloqueDeuda). */
  deudaPorConfirmar: boolean;
  guardando: boolean;
  aplicando: boolean;
  forma: FormaElegida | null;
  onForma: (forma: FormaElegida) => void;
  /** El editor del encabezado, para «Corregir datos». */
  editorEncabezado: React.ReactNode;
  editandoEncabezado: boolean;
  onEditarEncabezado: (abierto: boolean) => void;
  /** El bloque de deuda completo, para «Otra forma». */
  bloqueDeuda: React.ReactNode;
  /** Los encargos que esta factura puede cerrar (0111). */
  encargos: React.ReactNode;
  onActualizarFila: (filaId: number, cambios: CambiosFila) => Promise<void>;
  onAgregarFila: (fila: NuevaFila) => Promise<void>;
  onBuscarProducto: (fila: FilaCompra) => void;
  /** Con lo que se reviso, para el resumen que se muestra al terminar. */
  onCargar: (propuestas: PropuestaParaGuardar[], resumen: ResumenDeCarga) => void;
}

const VARIANTE_MARCA: Record<MarcaLinea, "info" | "warning" | "success" | "secondary" | "outline"> =
  {
    NUEVO: "info",
    VUELVE: "warning",
    SUBE: "warning",
    BAJA: "warning",
    NO_BAJA: "success",
    SIN_CAMBIOS: "secondary",
    PRECIO_A_MANO: "outline",
    NO_ENTRA: "outline",
  };

const FORMAS: ReadonlyArray<{ forma: FormaDePago; texto: string }> = [
  { forma: "DEBO", texto: "No, se la debo" },
  { forma: "EFECTIVO", texto: "Sí, efectivo" },
  { forma: "BANCO", texto: "Sí, banco" },
  { forma: "MERCADO_PAGO", texto: "Sí, Mercado Pago" },
];

export function CompraEnUnaPantalla(props: CompraEnUnaPantallaProps): JSX.Element {
  const {
    compra,
    filas,
    proveedor,
    margenes,
    rubroSugeridoId,
    monedaProveedor,
    verificacion,
    sinCotizacionDe,
    forma,
  } = props;
  const [abierta, setAbierta] = useState<number | null>(null);
  const [agregando, setAgregando] = useState(() => esManual(compra) && filas.length === 0);
  // Lo que se escribio en el campo del precio y todavia no se reviso (0153).
  const [borradores, setBorradores] = useState<ReadonlyMap<number, number | null>>(new Map());

  const nombreProveedor = proveedor?.nombre ?? compra.proveedor_detectado ?? "el proveedor";
  const preguntas = useMemo(
    () => [...filas].filter(esPregunta).sort((a, b) => a.orden - b.orden),
    [filas],
  );
  const renglones = useMemo(
    () => [...filas].filter((f) => !esPregunta(f)).sort((a, b) => a.orden - b.orden),
    [filas],
  );
  const marcas = useMemo(
    () => new Map(renglones.map((f) => [f.id, marcaDeLinea(f, compra, monedaProveedor)])),
    [renglones, compra, monedaProveedor],
  );
  const propuestas = useMemo(
    () =>
      renglones.filter(esPropuestaNueva).map((f) => ({
        fila: f,
        alta: altaPropuesta(f, rubroSugeridoId, margenes),
      })),
    [renglones, rubroSugeridoId, margenes],
  );
  const sinRubro = propuestas.filter(({ alta }) => alta.rubroId === null);

  // 0153: el precio de venta de cada renglon que entra, con lo que se ve en el
  // campo y si ya se reviso.
  const precios = useMemo(() => {
    const m = new Map<
      number,
      { precio: PrecioDelRenglon; campo: number | null; revisado: boolean; alta: DatosAlta | null }
    >();
    for (const f of renglones) {
      const marca = marcas.get(f.id) ?? "SIN_CAMBIOS";
      const alta = propuestas.find((p) => p.fila.id === f.id)?.alta ?? null;
      const costo = costoDelProducto(f, compra, monedaProveedor);
      const precio = precioDelRenglon(f, marca, alta, costo, props.cotizacion);
      if (!precio) continue;
      const borrador = borradores.get(f.id);
      m.set(f.id, {
        precio,
        campo: precioEnElCampo(f, precio, borrador),
        revisado: precioRevisado(f, borrador),
        alta,
      });
    }
    return m;
  }, [renglones, marcas, propuestas, compra, monedaProveedor, props.cotizacion, borradores]);
  const porRevisar = renglones.filter((f) => {
    const p = precios.get(f.id);
    return p !== undefined && !p.revisado;
  });
  const sinValor = porRevisar.filter((f) => precios.get(f.id)?.campo === null);
  const aMano = renglones.filter(
    (f) => marcas.get(f.id) !== "NO_ENTRA" && aNumero(f.ingresado_a_mano_hoy ?? 0) > 0,
  );

  function cambiarCampo(filaId: number, valor: number | null): void {
    setBorradores((b) => new Map(b).set(filaId, valor));
  }
  function sinBorrador(filaId: number): void {
    setBorradores((b) => {
      const n = new Map(b);
      n.delete(filaId);
      return n;
    });
  }
  async function revisar(fila: FilaCompra): Promise<void> {
    const datos = precios.get(fila.id);
    if (!datos || datos.campo === null) return;
    const marca = marcas.get(fila.id) ?? "SIN_CAMBIOS";
    await props.onActualizarFila(
      fila.id,
      cambiosDePrecio(
        fila,
        marca,
        datos.campo,
        costoDelProducto(fila, compra, monedaProveedor),
        props.cotizacion,
      ),
    );
    sinBorrador(fila.id);
  }
  async function revisarTodos(): Promise<void> {
    for (const f of porRevisar) {
      if (precios.get(f.id)?.campo == null) continue;
      await revisar(f);
    }
  }

  const plegar = renglones.length > LINEAS_SIN_PLEGAR;
  const aLaVista = plegar
    ? renglones.filter((f) => esParaMirar(marcas.get(f.id) ?? "SIN_CAMBIOS"))
    : renglones;
  const plegados = plegar
    ? renglones.filter((f) => !esParaMirar(marcas.get(f.id) ?? "SIN_CAMBIOS"))
    : [];

  const noBajan = plegados.filter((f) => marcas.get(f.id) === "NO_BAJA").length;
  const plegadosSinRevisar = plegados.filter((f) => porRevisar.includes(f)).length;
  const entran = renglones.filter((f) => marcas.get(f.id) !== "NO_ENTRA");
  const unidades = entran.reduce((t, f) => t + cantidadEfectiva(f), 0);

  const motivo =
    preguntas.length > 0
      ? preguntas.length === 1
        ? "Contestá la pregunta"
        : `Contestá las ${preguntas.length} preguntas`
      : sinRubro.length > 0
        ? sinRubro.length === 1
          ? "Elegí el rubro del producto nuevo"
          : `Elegí el rubro de los ${sinRubro.length} productos nuevos`
        : sinValor.length > 0
          ? sinValor.length === 1
            ? "Escribí el precio de venta que falta"
            : `Escribí los ${sinValor.length} precios de venta que faltan`
          : porRevisar.length > 0
            ? porRevisar.length === 1
              ? "Revisá el precio de venta"
              : `Revisá los ${porRevisar.length} precios de venta`
            : sinCotizacionDe !== null
              ? "Falta el dólar de la factura"
              : !verificacion.cierra
                ? "Revisá el total de la factura"
                : entran.length === 0
                  ? "No hay productos para cargar"
                  : props.encargosSinLeer
                    ? "Recargá la página para ver los encargos"
                    : forma === null
                      ? "Contestá si la pagaste"
                      : props.deudaPorConfirmar
                        ? "Confirmá el importe de la deuda"
                        : null;

  function cargar(): void {
    props.onCargar(
      propuestas.map(({ fila, alta }) => ({ filaId: fila.id, cambios: cambiosDeAlta(alta) })),
      armarResumen(),
    );
  }

  /** Lo que se muestra en el popup al terminar (pedido del 05/11/2026). */
  function armarResumen(): ResumenDeCarga {
    const r: ResumenDeCarga = {
      proveedor: nombreProveedor,
      referencia: referenciaDe(compra),
      stock: [],
      precios: [],
      precioIgual: 0,
      nuevos: [],
      aprendido: [],
      afuera: [],
      plata:
        forma === "DEBO" || forma === null
          ? `Le debés a ${nombreProveedor}`
          : forma === "EFECTIVO"
            ? "Salió de la caja de efectivo"
            : forma === "BANCO"
              ? "Salió del banco"
              : forma === "MERCADO_PAGO"
                ? "Salió de Mercado Pago"
                : "El pago que cargaste",
      total:
        (compra.total_a_pagar ?? compra.total_declarado) == null
          ? null
          : aNumero(compra.total_a_pagar ?? compra.total_declarado),
      moneda: compra.moneda ?? "ARS",
    };
    for (const f of renglones) {
      const marca = marcas.get(f.id) ?? "SIN_CAMBIOS";
      if (marca === "NO_ENTRA") {
        r.afuera.push(f.descripcion);
        continue;
      }
      const datos = precios.get(f.id);
      const nombre =
        marca === "NUEVO"
          ? (datos?.alta?.nombre ?? f.nuevo_nombre ?? f.descripcion)
          : (f.producto_nombre ?? f.descripcion);
      r.stock.push({ nombre, cantidad: cantidadEfectiva(f) });
      if (marca === "NUEVO") {
        const rubroId = datos?.alta?.rubroId ?? f.nuevo_rubro_id;
        r.nuevos.push({
          nombre,
          rubro: props.rubros.find((x) => x.id === rubroId)?.nombre ?? null,
        });
        if (datos?.campo != null) {
          r.precios.push({ nombre, antes: null, ahora: datos.campo, moneda: datos.precio.moneda });
        }
        continue;
      }
      if (
        datos &&
        datos.campo !== null &&
        datos.precio.hoy !== null &&
        datos.campo !== datos.precio.hoy
      ) {
        r.precios.push({
          nombre,
          antes: datos.precio.hoy,
          ahora: datos.campo,
          moneda: datos.precio.moneda,
        });
      } else {
        r.precioIgual += 1;
      }
      if (f.match_tipo === "MANUAL") r.aprendido.push({ remito: f.descripcion, producto: nombre });
    }
    return r;
  }

  async function decidir(fila: FilaCompra, cambios: CambiosFila): Promise<void> {
    await props.onActualizarFila(fila.id, cambios);
    setAbierta(null);
  }

  const pregunta = preguntas[0] ?? null;

  return (
    <div className="space-y-4">
      <EncabezadoDeLaCompra
        compra={compra}
        proveedor={nombreProveedor}
        cuentas={props.cuentas}
        forma={forma}
        onForma={props.onForma}
        editando={props.editandoEncabezado}
        onEditar={props.onEditarEncabezado}
        editor={props.editorEncabezado}
        bloqueDeuda={props.bloqueDeuda}
        deudaPorConfirmar={props.deudaPorConfirmar}
      />

      {sinCotizacionDe !== null && !props.editandoEncabezado ? (
        <Aviso
          tono="warning"
          accion={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => props.onEditarEncabezado(true)}
            >
              Cargar el dólar
            </Button>
          }
        >
          Falta el dólar que te tomó {sinCotizacionDe}. Sin él no se puede calcular el costo.
        </Aviso>
      ) : null}

      {!verificacion.cierra && !props.editandoEncabezado ? (
        <Aviso
          tono="warning"
          accion={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => props.onEditarEncabezado(true)}
            >
              Corregir el total
            </Button>
          }
        >
          Los productos suman{" "}
          <Importe monto={verificacion.calculado} moneda={compra.moneda ?? "ARS"} tamano="sm" /> y
          la factura dice{" "}
          <Importe
            monto={verificacion.declarado ?? 0}
            moneda={compra.moneda ?? "ARS"}
            tamano="sm"
          />
          . Corregí la línea que está mal; si es un flete, sacá el total de la factura.
        </Aviso>
      ) : null}

      {pregunta ? (
        <PreguntaDeLinea
          key={pregunta.id}
          fila={pregunta}
          total={preguntas.length}
          {...props}
          abierta={abierta === pregunta.id}
          onAbrir={(abrir) => setAbierta(abrir ? pregunta.id : null)}
          onDecidir={(cambios) => void decidir(pregunta, cambios)}
        />
      ) : null}

      {aLaVista.length > 0 ? (
        <section aria-labelledby="t-para-mirar" className="space-y-1">
          {plegar ? (
            <h3 id="t-para-mirar" className="text-sm font-semibold">
              Para mirar{" "}
              <span className="font-normal text-muted-foreground">({aLaVista.length})</span>
            </h3>
          ) : (
            <h3 id="t-para-mirar" className="sr-only">
              Los productos
            </h3>
          )}
          <Card padding="none">
            <BarraDeRevision
              total={precios.size}
              revisados={precios.size - porRevisar.length}
              guardando={props.guardando}
              onTodos={
                precios.size > 1 && preguntas.length === 0 ? () => void revisarTodos() : null
              }
            />
            <ul className="divide-y divide-border">
              {aLaVista.map((f) => (
                <RenglonDeCompra
                  key={f.id}
                  fila={f}
                  marca={marcas.get(f.id) ?? "SIN_CAMBIOS"}
                  alta={propuestas.find((p) => p.fila.id === f.id)?.alta ?? null}
                  abierto={abierta === f.id}
                  onAbrir={(abrir) => setAbierta(abrir ? f.id : null)}
                  onDecidir={(cambios) => void decidir(f, cambios)}
                  precio={precios.get(f.id)?.precio ?? null}
                  campo={precios.get(f.id)?.campo ?? null}
                  revisado={precios.get(f.id)?.revisado ?? true}
                  onCampo={(v) => cambiarCampo(f.id, v)}
                  onRevisar={() => void revisar(f)}
                  {...props}
                />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      {plegados.length > 0 ? (
        // La tarjeta pone el fondo; el <details> pliega, como en FlujoLineas.
        <Card padding="none" variant="muted">
          <details className="group">
            <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm">
              <ChevronRight
                className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
                aria-hidden="true"
              />
              <span>
                <strong className="font-semibold">{plegados.length} sin novedad</strong> · reponen{" "}
                {mostrarCantidad(plegados.reduce((t, f) => t + cantidadEfectiva(f), 0))} unidades
              </span>
              {noBajan > 0 ? (
                <Badge variant="success">
                  {noBajan} no {noBajan === 1 ? "baja" : "bajan"}
                </Badge>
              ) : null}
              {plegadosSinRevisar > 0 ? (
                <Badge variant="warning">
                  {plegadosSinRevisar} {plegadosSinRevisar === 1 ? "precio" : "precios"} sin revisar
                </Badge>
              ) : null}
            </summary>
            <ul className="divide-y divide-border border-t border-border bg-card">
              {plegados.map((f) => (
                <RenglonDeCompra
                  key={f.id}
                  fila={f}
                  marca={marcas.get(f.id) ?? "SIN_CAMBIOS"}
                  alta={null}
                  abierto={abierta === f.id}
                  onAbrir={(abrir) => setAbierta(abrir ? f.id : null)}
                  onDecidir={(cambios) => void decidir(f, cambios)}
                  precio={precios.get(f.id)?.precio ?? null}
                  campo={precios.get(f.id)?.campo ?? null}
                  revisado={precios.get(f.id)?.revisado ?? true}
                  onCampo={(v) => cambiarCampo(f.id, v)}
                  onRevisar={() => void revisar(f)}
                  {...props}
                />
              ))}
            </ul>
          </details>
        </Card>
      ) : null}

      {agregando ? (
        <LineaManual
          moneda={compra.moneda ?? "ARS"}
          cotizacion={aNumero(compra.cotizacion) || props.cotizacion}
          proveedorId={compra.proveedor_id}
          guardando={props.guardando}
          onAgregar={async (fila) => {
            await props.onAgregarFila(fila);
            if (!esManual(compra)) setAgregando(false);
          }}
          onCancelar={() => setAgregando(false)}
        />
      ) : (
        <Button
          type="button"
          variant="dashed"
          onClick={() => setAgregando(true)}
          disabled={props.guardando}
        >
          <PlusCircle aria-hidden="true" />
          Agregar un producto
        </Button>
      )}

      {props.encargos}

      {aMano.length > 0 ? (
        // El 04/11/2026 cargaron por conteo los vapers de LM Imports y la compra
        // los iba a sumar otra vez (0153).
        <Aviso
          tono="warning"
          accion={
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={props.guardando}
              onClick={() =>
                void (async () => {
                  for (const f of aMano) await props.onActualizarFila(f.id, { accion: "IGNORAR" });
                })()
              }
            >
              {aMano.length === 1 ? "Dejarla afuera del stock" : "Dejarlas afuera del stock"}
            </Button>
          }
        >
          <span className="inline-flex items-start gap-2">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              Ya entraron a mano:{" "}
              {aMano
                .map(
                  (f) =>
                    `${f.producto_nombre ?? f.descripcion} ×${mostrarCantidad(aNumero(f.ingresado_a_mano_hoy ?? 0))}`,
                )
                .join(", ")}
              . Si es esta misma mercadería, cargarla la suma dos veces.
            </span>
          </span>
        </Aviso>
      ) : null}

      <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-background px-4 py-3 sm:mx-0 sm:rounded-lg sm:border">
        <div className="flex flex-col items-stretch gap-1 sm:items-end">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {porRevisar.length > 0 &&
            preguntas.length === 0 &&
            sinValor.length < porRevisar.length ? (
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={props.guardando}
                onClick={() => void revisarTodos()}
              >
                <CheckCheck aria-hidden="true" />
                {porRevisar.length === 1
                  ? "Está bien el precio"
                  : `Están bien los ${porRevisar.length} precios`}
              </Button>
            ) : null}
            <Button
              type="button"
              size="lg"
              disabled={motivo !== null || props.guardando}
              cargando={props.aplicando}
              onClick={cargar}
            >
              {motivo ??
                `Cargar ${mostrarCantidad(unidades)} ${unidades === 1 ? "unidad" : "unidades"} · ${textoDeLaForma(forma ?? "DEBO", nombreProveedor)}`}
            </Button>
          </div>
          {motivo === null ? (
            <span className="text-center text-xs text-muted-foreground sm:text-right">
              Después podés deshacer la carga.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Arriba: quién, cuánto y si se pagó
// ─────────────────────────────────────────────────────────────────────────────

function EncabezadoDeLaCompra({
  compra,
  proveedor,
  cuentas,
  forma,
  onForma,
  editando,
  onEditar,
  editor,
  bloqueDeuda,
  deudaPorConfirmar,
}: {
  compra: Compra;
  proveedor: string;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  forma: FormaElegida | null;
  onForma: (forma: FormaElegida) => void;
  editando: boolean;
  onEditar: (abierto: boolean) => void;
  editor: React.ReactNode;
  bloqueDeuda: React.ReactNode;
  deudaPorConfirmar: boolean;
}): JSX.Element {
  const moneda = compra.moneda ?? "ARS";
  const total = compra.total_declarado === null ? null : aNumero(compra.total_declarado);
  const formas = FORMAS.filter(
    (f) => f.forma === "DEBO" || cuentaParaPagar(f.forma, cuentas, moneda) !== null,
  );
  const falta = forma === null;
  const consecuencia =
    forma === null || forma === "OTRA"
      ? null
      : forma === "DEBO"
        ? `Queda una deuda con ${proveedor}.`
        : forma === "EFECTIVO"
          ? "Sale de la caja de efectivo."
          : forma === "BANCO"
            ? "Sale del banco."
            : "Sale de Mercado Pago.";

  return (
    <Card padding="normal" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <TituloSeccion nivel="ficha">{proveedor}</TituloSeccion>
          <p className="text-sm text-muted-foreground">
            {referenciaDe(compra)}
            {compra.fecha_comprobante
              ? ` · ${compra.fecha_comprobante.split("-").reverse().join("/")}`
              : ""}
          </p>
        </div>
        {total !== null ? (
          <div className="grid justify-items-start sm:justify-items-end">
            <span className="text-xs text-muted-foreground">Total</span>
            <Importe monto={total} moneda={moneda} className="text-xl font-semibold" />
          </div>
        ) : null}
      </div>

      {editando ? editor : null}

      <div
        className="space-y-2 border-t border-border pt-3"
        role="group"
        aria-labelledby="t-la-pagaste"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="t-la-pagaste" className={cn("text-sm font-semibold", falta && "text-warning")}>
            ¿La pagaste?
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={() => onEditar(!editando)}>
            {editando ? "Listo" : "Corregir datos"}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {formas.map((f) => (
            <Button
              key={f.forma}
              type="button"
              variant={forma === f.forma ? "default" : "outline"}
              aria-pressed={forma === f.forma}
              className={cn("px-2", falta && "border-warning")}
              onClick={() => onForma(f.forma)}
            >
              {f.texto}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">{consecuencia ?? ""}</span>
          <Button
            type="button"
            variant={forma === "OTRA" ? "secondary" : "outline"}
            size="sm"
            aria-pressed={forma === "OTRA"}
            onClick={() => onForma("OTRA")}
          >
            Una parte, varias cajas u otro importe
          </Button>
        </div>
        {forma === "OTRA" || deudaPorConfirmar ? bloqueDeuda : null}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las dudas, de a una
// ─────────────────────────────────────────────────────────────────────────────

function PreguntaDeLinea({
  fila,
  total,
  abierta,
  onAbrir,
  onDecidir,
  ...props
}: CompraEnUnaPantallaProps & {
  fila: FilaCompra;
  total: number;
  abierta: boolean;
  onAbrir: (abrir: boolean) => void;
  onDecidir: (cambios: CambiosFila) => void;
}): JSX.Element {
  return (
    <Card padding="normal" className="space-y-3 border-2 border-primary">
      {total > 1 ? <p className="text-xs text-muted-foreground">1 de {total}</p> : null}
      <p className="text-sm text-muted-foreground">El remito dice «{fila.descripcion}»</p>
      <p>
        ¿Es tu <strong>{fila.producto_nombre}</strong>?
      </p>
      {abierta ? (
        <AltaDeLinea
          fila={fila}
          {...props}
          onCancelar={() => onAbrir(false)}
          onCrear={(alta) => onDecidir(cambiosDeAlta(alta))}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={props.guardando}
            onClick={() => onDecidir({ accion: "INGRESAR", producto_id: fila.producto_id })}
          >
            Sí
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={props.guardando}
            onClick={() => props.onBuscarProducto(fila)}
          >
            Es otro de mis productos
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={props.guardando}
            onClick={() => onAbrir(true)}
          >
            Es un producto nuevo
          </Button>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Un renglón por producto
// ─────────────────────────────────────────────────────────────────────────────

function RenglonDeCompra({
  fila,
  marca: marcaBase,
  alta,
  abierto,
  onAbrir,
  onDecidir,
  precio,
  campo,
  revisado,
  onCampo,
  onRevisar,
  ...props
}: CompraEnUnaPantallaProps & {
  fila: FilaCompra;
  marca: MarcaLinea;
  alta: DatosAlta | null;
  abierto: boolean;
  onAbrir: (abrir: boolean) => void;
  onDecidir: (cambios: CambiosFila) => void;
  precio: PrecioDelRenglon | null;
  campo: number | null;
  revisado: boolean;
  onCampo: (valor: number | null) => void;
  onRevisar: () => void;
}): JSX.Element {
  const [creando, setCreando] = useState(false);
  const nombre =
    marcaBase === "NUEVO"
      ? (alta?.nombre ?? fila.nuevo_nombre ?? fila.descripcion)
      : (fila.producto_nombre ?? fila.descripcion);
  const costo = costoDelProducto(fila, props.compra, props.monedaProveedor);
  const marca = marcaConPrecio(marcaBase, precio?.hoy ?? null, campo);
  const sinRubro = marcaBase === "NUEVO" && (alta?.rubroId ?? fila.nuevo_rubro_id) === null;
  const rubro =
    marcaBase === "NUEVO"
      ? (props.rubros.find((r) => r.id === (alta?.rubroId ?? fila.nuevo_rubro_id))?.nombre ?? null)
      : null;

  // Abajo, en gris, solo lo que aporta: cómo lo llama el remito si no se
  // parece al nombre propio, y el costo si cambió o es nuevo.
  const detalle: React.ReactNode[] = [];
  if (marcaBase !== "NO_ENTRA" && !contiene(nombre, fila.descripcion)) {
    detalle.push(<span key="r">remito: «{fila.descripcion}»</span>);
  }
  if (marcaBase === "NUEVO") {
    detalle.push(
      <span key="c">
        costo <Importe monto={costo.costo} moneda={costo.moneda} tamano="sm" />
        {rubro ? ` · va a ${rubro}` : ""}
      </span>,
    );
  } else if (
    marcaBase !== "NO_ENTRA" &&
    fila.costo_anterior !== null &&
    fila.moneda_anterior === costo.moneda &&
    aNumero(fila.costo_anterior) !== costo.costo
  ) {
    detalle.push(
      <span key="c" className="whitespace-nowrap">
        costo <Importe monto={aNumero(fila.costo_anterior)} moneda={costo.moneda} tamano="sm" /> →{" "}
        <Importe monto={costo.costo} moneda={costo.moneda} tamano="sm" />
      </span>,
    );
  }

  return (
    <li
      className={cn(
        "px-3 py-3 sm:px-4",
        "border-l-2",
        precio && !revisado ? "border-l-warning/40" : "border-l-transparent",
      )}
      data-testid={`renglon-${fila.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="w-10 shrink-0 pt-0.5 text-right text-sm font-semibold tabular-nums">
            {marcaBase === "NO_ENTRA" ? "" : `+${mostrarCantidad(cantidadEfectiva(fila))}`}
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-medium">{nombre}</span>
              <Badge variant={VARIANTE_MARCA[marca]}>{TEXTO_MARCA[marca]}</Badge>
            </div>
            {detalle.length > 0 ? (
              <div className="flex flex-wrap gap-x-1 text-xs text-muted-foreground">
                {detalle.flatMap((d, i) => (i === 0 ? [d] : [<span key={`s${i}`}>·</span>, d]))}
              </div>
            ) : null}
            <div className="-ml-2 flex flex-wrap gap-1">
              {/* «Ya lo tengo» a la vista: escondido detrás de «Poner precio», el
                  local no pudo vincular los vapers de LM Imports (04/11/2026). */}
              {marcaBase === "NUEVO" || marcaBase === "NO_ENTRA" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={props.guardando}
                  onClick={() => props.onBuscarProducto(fila)}
                >
                  <Search aria-hidden="true" />
                  Ya lo tengo
                </Button>
              ) : null}
              {sinRubro ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={abierto}
                  onClick={() => {
                    setCreando(true);
                    onAbrir(true);
                  }}
                >
                  Elegí el rubro
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-expanded={abierto}
                onClick={() => {
                  setCreando(false);
                  onAbrir(!abierto);
                }}
              >
                {abierto ? "Cerrar" : "Cambiar"}
              </Button>
            </div>
          </div>
        </div>
        {precio ? (
          <CampoDePrecio
            fila={fila}
            nombre={nombre}
            precio={precio}
            campo={campo}
            revisado={revisado}
            costo={costo}
            cotizacion={props.cotizacion}
            guardando={props.guardando}
            onCampo={onCampo}
            onRevisar={onRevisar}
          />
        ) : null}
      </div>
      {abierto ? (
        <Card variant="muted" padding="compacto" className="mt-3 space-y-3">
          {creando ? (
            <AltaDeLinea
              fila={
                alta ? { ...fila, nuevo_nombre: alta.nombre, nuevo_rubro_id: alta.rubroId } : fila
              }
              {...props}
              onCancelar={() => setCreando(false)}
              onCrear={(nueva) => onDecidir(cambiosDeAlta(nueva))}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span>«{fila.descripcion}»</span>
                <span className="tabular-nums">
                  {mostrarCantidad(aNumero(fila.cantidad))} ×{" "}
                  <Importe monto={aNumero(fila.costo_final)} moneda={fila.moneda} tamano="sm" />
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {marca === "NUEVO" ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => props.onBuscarProducto(fila)}
                    >
                      Ya lo tengo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCreando(true)}
                    >
                      Cambiar nombre o precio
                    </Button>
                  </>
                ) : marca === "NO_ENTRA" ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => props.onBuscarProducto(fila)}
                    >
                      Es uno que ya tengo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCreando(true)}
                    >
                      Es un producto nuevo
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => props.onBuscarProducto(fila)}
                    >
                      Es otro de mis productos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCreando(true)}
                    >
                      Es un producto nuevo
                    </Button>
                  </>
                )}
                {marca !== "NO_ENTRA" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={props.guardando}
                    onClick={() => onDecidir({ accion: "IGNORAR" })}
                  >
                    No entra al stock
                  </Button>
                ) : null}
              </div>
              {marca !== "NO_ENTRA" ? (
                <>
                  <CantidadRecibida
                    fila={fila}
                    guardando={props.guardando}
                    onCantidad={(recibida, motivo) =>
                      void props.onActualizarFila(fila.id, {
                        cantidad_recibida: recibida,
                        motivo_diferencia: motivo,
                      })
                    }
                    onCorregir={
                      esManual(props.compra)
                        ? (cantidad) => void props.onActualizarFila(fila.id, { cantidad })
                        : undefined
                    }
                  />
                  <OtroPrecio
                    fila={fila}
                    guardando={props.guardando}
                    onPrecio={(costoNeto) =>
                      void props.onActualizarFila(fila.id, { costo_neto: costoNeto })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    «No entra al stock» no cambia lo que le debés al proveedor.
                  </p>
                </>
              ) : null}
            </>
          )}
        </Card>
      ) : null}
    </li>
  );
}

/**
 * El precio de venta del renglon: el campo, «Está bien», el sugerido y el de
 * hoy. Revisarlo guarda el precio en la linea; escribir otro encima lo vuelve
 * a «sin revisar» hasta que se lo confirma (0153).
 */
function CampoDePrecio({
  fila,
  nombre,
  precio,
  campo,
  revisado,
  costo,
  cotizacion,
  guardando,
  onCampo,
  onRevisar,
}: {
  fila: FilaCompra;
  nombre: string;
  precio: PrecioDelRenglon;
  campo: number | null;
  revisado: boolean;
  costo: { costo: number; moneda: Moneda };
  cotizacion: number;
  guardando: boolean;
  onCampo: (valor: number | null) => void;
  onRevisar: () => void;
}): JSX.Element {
  const [texto, setTexto] = useState<string | null>(null);
  const mostrado = texto ?? (campo === null ? "" : formatearPrecio(campo));
  const margen = campo === null ? null : margenDelPrecio(campo, costo, precio.moneda, cotizacion);
  const id = `precio-${fila.id}`;
  const { sugerido, hoy } = precio;
  const sugeridoMasBajo = sugerido !== null && hoy !== null && sugerido < hoy;

  return (
    <div className="flex shrink-0 flex-col gap-1 pl-12 sm:items-end sm:pl-0">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        Precio de venta
      </label>
      <div className="flex items-center gap-2">
        <ChipMoneda moneda={precio.moneda} />
        <Input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          className="h-9 w-28 text-right font-semibold tabular-nums sm:h-9"
          value={mostrado}
          aria-label={`Precio de venta de ${nombre}`}
          onChange={(e) => {
            setTexto(e.target.value);
            const v = parsearImporte(e.target.value);
            onCampo(v !== null && v > 0 ? v : null);
          }}
          onBlur={() => setTexto(null)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && campo !== null && !guardando) {
              e.preventDefault();
              setTexto(null);
              onRevisar();
            }
          }}
        />
        <Button
          type="button"
          variant={revisado ? "secondary" : "outline"}
          size="sm"
          aria-pressed={revisado}
          disabled={guardando || campo === null}
          onClick={() => (revisado ? null : onRevisar())}
          className={cn("h-9 sm:h-9", revisado && "text-success hover:text-success")}
        >
          {revisado ? (
            <>
              <Check aria-hidden="true" />
              Revisado
            </>
          ) : (
            <>
              <span
                className="inline-block size-3.5 rounded-sm border-2 border-warning"
                aria-hidden="true"
              />
              Está bien
            </>
          )}
        </Button>
      </div>
      <div className="space-y-0.5 text-xs text-muted-foreground sm:text-right">
        {margen !== null ? (
          <p>
            {campo === sugerido ? "sugerido · " : ""}margen{" "}
            <span className={cn("tabular-nums", margen < 20 && "font-semibold text-warning")}>
              {formatearNumero(margen, 0)}%
            </span>
          </p>
        ) : campo === null ? (
          <p className="text-warning">Escribí el precio de venta.</p>
        ) : null}
        {sugerido !== null && campo !== sugerido ? (
          <p>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs font-normal text-muted-foreground underline hover:text-foreground sm:h-auto"
              onClick={() => {
                setTexto(null);
                onCampo(sugerido);
              }}
            >
              sugerido <Importe monto={sugerido} moneda={precio.moneda} tamano="sm" />
            </Button>
          </p>
        ) : null}
        {hoy !== null && campo !== hoy ? (
          <p>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs font-normal text-muted-foreground underline hover:text-foreground sm:h-auto"
              onClick={() => {
                setTexto(null);
                onCampo(hoy);
              }}
            >
              dejar el de hoy, <Importe monto={hoy} moneda={precio.moneda} tamano="sm" />
            </Button>
          </p>
        ) : hoy !== null ? (
          <p>igual que hoy</p>
        ) : null}
        {sugeridoMasBajo && campo === hoy ? (
          <p>
            El sugerido es más bajo (
            <Importe monto={sugerido} moneda={precio.moneda} tamano="sm" />
            ): no baja solo.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** «1.085» o «1.085,50»: sin decimales cuando no los tiene. */
function formatearPrecio(valor: number): string {
  return formatearNumero(valor, Number.isInteger(valor) ? 0 : 2);
}

/** Arriba de la lista: cuantos precios faltan y «Están bien todos». */
function BarraDeRevision({
  total,
  revisados,
  guardando,
  onTodos,
}: {
  total: number;
  revisados: number;
  guardando: boolean;
  onTodos: (() => void) | null;
}): JSX.Element | null {
  if (total === 0) return null;
  const faltan = total - revisados;
  return (
    <Aviso
      tono={faltan > 0 ? "warning" : "success"}
      compacto
      icono={null}
      className="rounded-b-none border-x-0 border-t-0"
      accion={
        faltan > 0 && onTodos ? (
          <Button type="button" variant="outline" size="sm" disabled={guardando} onClick={onTodos}>
            <CheckCheck aria-hidden="true" />
            Están bien todos
          </Button>
        ) : undefined
      }
    >
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={cn("text-sm font-semibold", faltan > 0 ? "text-warning" : "text-success")}>
          {faltan === 0
            ? "Precios revisados"
            : faltan === 1
              ? "Falta revisar 1 precio"
              : `Faltan revisar ${faltan} precios`}
        </span>
        <span
          className="inline-block h-1.5 w-24 overflow-hidden rounded-full bg-muted"
          aria-hidden="true"
        >
          <span
            className="block h-full bg-success"
            style={{ width: `${Math.round((revisados / total) * 100)}%` }}
          />
        </span>
        <span className="tabular-nums text-muted-foreground">
          {revisados} de {total}
        </span>
      </span>
    </Aviso>
  );
}

function AltaDeLinea({
  fila,
  onCrear,
  onCancelar,
  ...props
}: CompraEnUnaPantallaProps & {
  fila: FilaCompra;
  onCrear: (alta: DatosAlta) => void;
  onCancelar: () => void;
}): JSX.Element {
  const conMargen =
    fila.nuevo_markup_pct === null && fila.nuevo_precio_manual === null
      ? (() => {
          const alta = altaPropuesta(fila, props.rubroSugeridoId, props.margenes);
          return alta.markup === null ? fila : { ...fila, nuevo_markup_pct: alta.markup };
        })()
      : fila;
  return (
    <AltaProducto
      fila={conMargen}
      rubros={props.rubros}
      rubroSugeridoId={props.rubroSugeridoId}
      cotizacion={props.cotizacion}
      costoProducto={costoDelProducto(fila, props.compra, props.monedaProveedor)}
      guardando={props.guardando}
      onCrear={onCrear}
      onCancelar={onCancelar}
    />
  );
}

/** Si el nombre propio ya dice lo mismo que el remito (palabra por palabra). */
function contiene(nombre: string, remito: string): boolean {
  const palabras = (s: string) =>
    ` ${s
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim()} `;
  return palabras(nombre).includes(palabras(remito));
}
